import { useQuery } from '@tanstack/react-query';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api/axios';

import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import { SendLaterFields } from './SendLaterFields';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { OutboundCallNodeData } from '@repo/shared-types';

interface OutboundCallConfigProps {
  data: OutboundCallNodeData;
  onChange: (data: OutboundCallNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

function useConfigurations() {
  return useQuery({
    queryKey: ['configurations'],
    queryFn: async () => {
      const res = await apiClient.get<{ id: string; name: string }[]>('/configurations');
      return res.data;
    },
  });
}

export const OutboundCallConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: OutboundCallConfigProps) => {
  const { data: configurations } = useConfigurations();

  const update = (patch: Partial<OutboundCallNodeData>) => onChange({ ...data, ...patch });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <VariableAutocomplete
          value={data.label}
          onChange={(v) => update({ label: v })}
          placeholder="Node label"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          analyticsNodeMetas={analyticsNodeMetas}
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Transport</label>
        <Select
          value={data.transport ?? 'pstn'}
          onValueChange={(v) => update({ transport: v as 'pstn' | 'whatsapp' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pstn">Phone (PSTN)</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-[10px]">
          PSTN uses Twilio/Telnyx. WhatsApp initiates a call through WhatsApp Business API.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Configuration *</label>
        <select
          value={data.configurationId}
          onChange={(e) => update({ configurationId: e.target.value })}
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
        >
          <option value="">Select configuration...</option>
          {configurations?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">To (phone number)</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.to}
            onChange={(v) => update({ to: v })}
            placeholder="{{contact.phone}}"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            className="flex-1"
          />
          <VariablePicker
            onSelect={(v) => update({ to: data.to + v })}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">From Number (optional)</label>
        <VariableAutocomplete
          value={data.fromNumber ?? ''}
          onChange={(v) => update({ fromNumber: v || undefined })}
          placeholder="Leave empty for default"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          analyticsNodeMetas={analyticsNodeMetas}
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Default Country Code</label>
        <input
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
          value={data.defaultCountryCode ?? ''}
          onChange={(e) => update({ defaultCountryCode: e.target.value || undefined })}
          placeholder="+44 (used when number starts with 0)"
        />
        <p className="text-muted-foreground text-[10px]">
          If the phone starts with 0, this code replaces it (e.g. +44 turns 07xxx into +447xxx)
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Delay (minutes)</label>
        <input
          type="number"
          min={0}
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
          value={data.delayMinutes ?? 0}
          onChange={(e) => update({ delayMinutes: parseInt(e.target.value, 10) || 0 })}
          placeholder="0"
        />
        <p className="text-muted-foreground text-[10px]">
          How long to wait before placing the call. 0 = immediate. Ignored when a send time is set
          below.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Schedule</label>
        <SendLaterFields
          value={{ sendAt: data.sendAt, offsetMinutes: data.offsetMinutes }}
          onChange={(fields) => update(fields)}
          immediateHint="use the delay above"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          analyticsNodeMetas={analyticsNodeMetas}
        />
      </div>
    </div>
  );
};
